import { Attr, BelongsTo, HasMany, HasOne, Model } from "spraypaint";

import { type Region } from "shared/services/appConfig/types";
import { ApplicationRecord } from "shared/services/spraypaint";
import type { Nullable } from "shared/types";

import type ActiveStorageAttachment from "./ActiveStorageAttachment";
import type BriefSet from "./BriefSet";
import type DistributionList from "./DistributionList";
import type OrganisationClient from "./OrganisationClient";
import type OrganisationContract from "./OrganisationContract";
import type OrganisationContractService from "./OrganisationContractService";
import type OrganisationOutreachLocationExclusion from "./OrganisationOutreachLocationExclusion";
import type OrganisationsFeature from "./OrganisationsFeature";
import type OrganisationsManager from "./OrganisationsManager";
import type OrganisationTeam from "./OrganisationTeam";
import type Stream from "./Stream";
import type TranscriptTurnaroundTime from "./TranscriptTurnaroundTime";
import type UpsellRegistration from "./UpsellRegistration";
import type User from "./User";
import type XeroContact from "./XeroContact";

enum Status {
  draft = "draft",
  live = "live",
  suspended = "suspended",
}

@Model()
class Organisation extends ApplicationRecord {
  static jsonapiType = "organisations";

  @Attr() abn: string;

  @Attr() acronyms: string[];

  @Attr() active: boolean;

  @Attr() activeUserCount: number;

  @Attr() address: string;

  @Attr({ persist: false }) admin: boolean;

  @Attr() aircallOption: string;

  @Attr() aiVisibilityTier: Nullable<string>;

  @Attr({ persist: false }) allowedPhoneCountryCodes: string[];

  @Attr({ persist: false }) archivedAt: string;

  @Attr() archivedReason: string;

  @HasMany() attachments: ActiveStorageAttachment[];

  @Attr() billable: boolean;

  @Attr() billingContactEmail: string;

  @Attr() billingContactName: string;

  @Attr() billingContactPhone: string;

  @Attr() billingNotes: string;

  @HasMany() briefSets: BriefSet[];

  @HasOne() businessDevelopmentManager: User;

  @Attr() businessDevelopmentManagerId: number;

  @Attr({ persist: false }) clientCount: number;

  @Attr() contentFlagsPackages: string;

  @Attr({ persist: false }) contractExpiry: string;

  @Attr() country: string;

  @Attr() cslPackage: string;

  @Attr() customHelpCenterContact: string;

  @Attr() customHelpMessage: string;

  @Attr() customSupportContact: string;

  @Attr() dailyReportRangeOffset: number;

  @Attr() dashboardDateRange: string;

  @Attr() defaultBriefSetId: number;

  @Attr() defaultCslRole: string;

  @Attr() defaultFilterSetId: number;

  @Attr() defaultOutreachRoleId: number;

  @Attr() defaultReportingUser: boolean;

  @Attr() defaultRoleId: number;

  @Attr() defaultSocialRoleId: number;

  @Attr() defaultTeamId: number;

  @Attr() demoMode: boolean;

  @HasMany() distributionLists: DistributionList[];

  @Attr() emailFromOption: string;

  @Attr() emailLanguage: string;

  @Attr() emailReplyToOption: string;

  @Attr() excludedKeywords: string[];

  @Attr() excludeFromCopyrightReports: boolean;

  @Attr() externalSource: string;

  @Attr() featureFlagsBatch: string;

  @Attr({ persist: false }) features: string[];

  @Attr() globalCustomerId: string;

  @Attr() governmentPortfolio: string;

  @Attr() gstExempt: boolean;

  @Attr({ persist: false }) hasAiLlmMonitoring: boolean;

  @Attr({ persist: false }) hasCsl: boolean;

  @Attr() helpCenterContactOption: string;

  @Attr() helpMessageOption: string;

  @Attr() industry: string;

  @Attr() intercomVisibility: string;

  @BelongsTo() keyContact: User;

  @Attr() keyContactEmail: string;

  @Attr() keyContactId: number;

  @Attr() keyContactName: string;

  @Attr({ persist: false }) kinetiqBroadcastCutoverAt: Nullable<string>;

  @Attr() language: string;

  @HasOne() latestContract: OrganisationContract;

  @Attr() legalEntityName: string;

  @Attr({ persist: false }) logoUrl: string;

  @Attr() maxClientsCount: number;

  @Attr() maxDateRange: number;

  @Attr() maxTeamsCount: number;

  @Attr() maxUsersCount: number;

  @Attr() mediaContactEmails: string[];

  @Attr() mentionStreamsCount: number;

  @Attr() movedToCompetitor: boolean;

  @Attr() name: string;

  @HasMany() notExpiredOrganisationContracts: OrganisationContract[];

  @HasOne() operationalTeam: OrganisationTeam;

  @Attr() operationalTeamId: number;

  @HasMany() organisationClients: OrganisationClient[];

  @HasMany() organisationContracts: OrganisationContract[];

  @Attr() organisationContractServices: OrganisationContractService[];

  @HasMany()
  organisationOutreachLocationExclusions: OrganisationOutreachLocationExclusion[];

  @HasMany() organisationsAccountManagers: OrganisationsManager[];

  @HasMany() organisationsFeatures: OrganisationsFeature[];

  @HasMany() organisationsManagers: OrganisationsManager[];

  @HasMany() organisationTeams: OrganisationTeam[];

  @Attr() postcode: string;

  @Attr() prAgency: boolean;

  @Attr() region: Region;

  @Attr() scheduleArchivedAt: string;

  @Attr() secondaryIndustry: string;

  @BelongsTo() secondaryKeyContact: User;

  @Attr() secondaryKeyContactId: number;

  @Attr() serviceLevel: string;

  @Attr() socialHelpCenterContactOption: string;

  @Attr() socialStreamsCount: number;

  @Attr() socialSupportContactOption: string;

  @Attr() state: string;

  @Attr() status: Status;

  @Attr() stockSymbol: string;

  @HasMany() streams: Stream[];

  @Attr() suburb: string;

  @Attr() supportContactOption: string;

  @Attr() teamCount: number;

  @Attr() timeZone: string;

  @HasMany() transcriptTurnaroundTimes: TranscriptTurnaroundTime[];

  @HasMany() upsellRegistrations: UpsellRegistration[];

  @HasMany() users: User[];

  @Attr() welcomeEmailBody: string;

  @Attr() welcomeEmailOption: string;

  @HasOne() xeroContact: XeroContact;

  @Attr() xeroRegion: string;

  archived() {
    return Boolean(this.archivedAt);
  }

  isCustomerSuccessTier() {
    return [
      "customer_success_tier_three",
      "customer_success_tier_four",
      "customer_success_tier_five",
    ].includes(this.serviceLevel);
  }

  isTierOnePlus() {
    return this.serviceLevel === "tier_one_plus";
  }

  live() {
    return this.status === Status.live;
  }

  notArchived() {
    return !this.archivedAt;
  }

  notLive() {
    return !this.live();
  }

  serviceLevelTitle() {
    let level = "";

    switch (this.serviceLevel) {
      case "tier_one_plus":
        level = "Tier 1+";
        break;
      case "standard_account_management_tier_one":
        level = "Tier 1";
        break;
      case "standard_account_management_tier_two":
        level = "Tier 2";
        break;
      case "standard_account_management_tier_three":
        level = "Tier 3";
        break;
      case "customer_success_tier_three":
        level = "Customer Success - Tier 3";
        break;
      case "customer_success_tier_four":
        level = "Customer Success - Tier 4";
        break;
      case "customer_success_tier_five":
        level = "Customer Success - Tier 5";
        break;
      case "tier_one":
        level = "Tier One";
        break;
      case "tier_two":
        level = "Tier Two";
        break;
      default:
    }

    return level;
  }
}

export default Organisation;
